import { useMutation, useQueryClient } from "@tanstack/react-query";
import { config } from "@/lib/config";

// Custom error class for auth-related errors
class AuthTokenExpiredError extends Error {
    constructor(message: string = "Your session has expired. Please log in again.") {
        super(message);
        this.name = "AuthTokenExpiredError";
    }
}

async function cancelAccessRequest(
    temporaryToken: string,
    requestId: string,
): Promise<void> {
    const response = await fetch(`${config.apiUrl}/api/v1/my/access-requests/${requestId}`, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${temporaryToken}`,
        },
    });

    if (!response.ok) {
        // Handle token expiration specifically
        if (response.status === 401) {
            // Dispatch event to notify auth context of session expiry
            window.dispatchEvent(new CustomEvent("auth:session_expired"));
            throw new AuthTokenExpiredError();
        }
        const data = await response.json();
        throw new Error(data.error || "Failed to cancel access request");
    }
}

export function useCancelMyAccessRequestMutation(temporaryToken: string | null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (requestId: string) => {
            if (!temporaryToken) {
                throw new Error("No temporary token available");
            }
            return cancelAccessRequest(temporaryToken, requestId);
        },
        onSuccess: () => {
            // Invalidate queries to refetch data
            queryClient.invalidateQueries({ queryKey: ["myAccessRequests"] });
            queryClient.invalidateQueries({ queryKey: ["availableTenants"] });
        },
    });
}
